import type { useTranslate } from "@/lib/i18n";
import type { SettlementMethod } from "@/lib/api/types";

type Translate = ReturnType<typeof useTranslate>;

/**
 * HOW A BILL WAS PAID, IN WORDS — the ONE helper every surface reads (D31, D44).
 *
 * It lives in `src/lib` and not inside a module because both sides of the app
 * name a settlement method: the TILL (the settled overlay, the customer's 80 mm
 * receipt) and the BACK OFFICE (invoice history and its filter, the invoice
 * detail, the settlement-correction picker and its refund warning, both shift
 * reviews, the X chit, and the Z on screen and on paper). While the rule was
 * written out three separate times, all three drifted, and they drifted in the
 * two worst directions at once:
 *
 * - `Receipt.methodLabel` and `manage/lib/invoiceDisplay`'s copy both ended
 *   `return method`, so `INSTAPAY` reached a CUSTOMER'S TAX RECEIPT as the raw
 *   enum token — «طريقة الدفع INSTAPAY» — on the same page whose drawer tile
 *   already says «تحويلات إنستاباي». Trap 9: the receipt is the original cheque.
 * - `OrderPanel`'s copy was a three-arm ternary DEFAULTING TO CASH, so a bill
 *   settled by bank transfer read «159.6 جنيه · كاش» on the till, directly above
 *   the refund button, with one `SETTLEMENT / INSTAPAY / 159.60` in the ledger
 *   and no cash anywhere near it. Naming a different real method is worse than
 *   printing an ugly token: a cashier asked "how did I pay?" answers wrongly.
 *
 * So this is one function, and `settlementMethodLabel.spec.ts` asserts it names
 * every member of its OWN `EVERY_METHOD` list in BOTH catalogues.
 *
 * **That is weaker than it used to claim, and the difference cost a method.**
 * This header read "a future method cannot be added and half-labelled again" —
 * but the list the spec walks is HAND-COPIED, not derived from the union, so
 * unit 4 added `AGGREGATOR` to the server enum, no client list gained it, and
 * the spec went on passing over the four it already knew about. Every surface
 * that named a settlement method would have printed the raw token, in Arabic
 * too. The list is only as good as the person editing it: adding a member means
 * editing the union in `api/types.ts`, the arm below, and `EVERY_METHOD` — and
 * the spec catches the second of the three, never the first. It is the same move the refund work made for
 * `REFUNDABLE_SETTLEMENT_METHODS` and `refundMethodCopy`.
 *
 * **The fallback is the raw value, and stays so.** `settlement_method` is a
 * free `text` column, not a Postgres enum, so a legacy or future row can carry
 * something this bundle has no word for — and a screen that rendered a BLANK
 * payment method would be worse than one that renders something ugly a reader
 * can at least ask about. What it must never do is GUESS: no arm here defaults
 * an unknown method to cash, card or anything else.
 */
export function settlementMethodLabel(
  t: Translate,
  method: SettlementMethod | string,
): string {
  if (method === "CASH") return t((m) => m.pos.cash);
  if (method === "CARD") return t((m) => m.pos.card);
  if (method === "INSTAPAY") return t((m) => m.pos.instapay);
  if (method === "WRITE_OFF") return t((m) => m.pos.writeOff);
  // F2. Added to the server enum by unit 4 and to no catalogue, so every screen
  // that named a settlement method printed the raw `AGGREGATOR` — including in
  // Arabic. The spec's `EVERY_METHOD` list is hand-copied, which is why adding
  // the member server-side did not fail it; it now carries this one too.
  if (method === "AGGREGATOR") return t((m) => m.pos.aggregatorMethod);
  return method;
}

/**
 * HOW A BILL WAS PAID WHEN IT WAS PAID MORE THAN ONE WAY (B117/B1).
 *
 * D93 moved the REPORTS onto the money ledger per tender and left every
 * bill-level screen reading `settlementMethod` — one string, the LAST tender's.
 * A 223.44 bill taken 100.00 on the card and then 123.44 in cash therefore said
 * «كاش» on the bill detail, on the till's settled overlay and on the history
 * row, while the Z put 100.00 of that same bill inside «بطاقة». Three screens
 * and the night's signed figure disagreeing about one bill.
 *
 * This is `settlementMethodLabel`'s plural, and it lives beside it for the same
 * reason that one does: the surfaces that need it are on both sides of the app,
 * and the last time a payment-method rule was written out per screen all the
 * copies drifted.
 *
 * `tenders` is the ledger's own answer and wins whenever it has one. It is
 * EMPTY on an open bill, on a write-off, and on every bill settled before the
 * ledger existed — those fall back to `method`, which reproduces exactly what
 * each of these screens printed before, so no filed history moves.
 *
 * The separator is « + », the same one the shift review's refund rows already
 * use for a refund that came back two ways (`shiftExceptions.ts`), so a reader
 * meets one idiom for "more than one method" across the app.
 */
export function tenderMethodsLabel(
  t: Translate,
  tenders: readonly (SettlementMethod | string)[],
  method: SettlementMethod | string | null,
): string | null {
  if (tenders.length > 0) {
    return tenders.map((tender) => settlementMethodLabel(t, tender)).join(" + ");
  }
  return method === null ? null : settlementMethodLabel(t, method);
}
